import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, makeStyles } from '@material-ui/core';
import KobeForm from './KobeForm/KobeForm';
import ZoneKobeForm from './ZoneKobeForm';
import Radio from '../Common/Form/Radio';
import RadioGroup from '../Common/Form/RadioGroup';
import SpiderForm from './SpiderForm';
import ExpectationsForm from './ExpectationsForm/ExpectationsForm';
import { AUTOMATION_STRATEGIES, LINE_SEGMENT_SPIDER } from '../../helpers/constants';
import { useUpdateAutomation } from '../../hooks/useUpdateAutomation';
import { useUpdateExpectations } from '../../hooks/useUpdateExpectations';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Inter',
    gridGap: theme.spacing(2.25),
    textAlign: 'left',
    '& .MuiFormLabel-root, & .MuiFormHelperText-root': {
      color: theme.palette.type === 'light' ? '#494949' : theme.palette.common.white,
    },
    '& .Mui-error': {
      color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
    },
  },
  strategySelectorContainer: {
    '& .MuiDivider-root': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  required: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
  },
  label: {
    marginBottom: theme.spacing(2),
    fontSize: '14px',
  },
  helperText: {
    fontSize: '14px',
    lineHeight: '1.2',
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(0.625),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.25),
  },
}));

const propTypes = {
  automationData: PropTypes.shape({
    current_strategy: PropTypes.object,
    strategies: PropTypes.shape({
      SPIDER: PropTypes.object,
      KOBE: PropTypes.object,
      KOBE_ZONE: PropTypes.object,
    }),
    proxy_events: PropTypes.shape({
      KOBE: PropTypes.arrayOf(
        PropTypes.shape({
          lobsang_event_id: PropTypes.number,
          event_date: PropTypes.string,
          performer: PropTypes.string,
          rank: PropTypes.number,
          similarity: PropTypes.number,
          venue: PropTypes.string,
        })
      ),
      KOBE_ZONE: PropTypes.arrayOf(
        PropTypes.shape({
          lobsang_event_id: PropTypes.number,
          event_date: PropTypes.string,
          performer: PropTypes.string,
          rank: PropTypes.number,
          similarity: PropTypes.number,
          venue: PropTypes.string,
        })
      ),
    }),
  }).isRequired,
  lobsangEventId: PropTypes.number.isRequired,
  kobeZones: PropTypes.arrayOf(PropTypes.string).isRequired,
};
const defaultProps = {};

const getInitialStrategy = automationData => {
  const currentStrategy = automationData?.current_strategy;
  const validStrategies = Object.values(AUTOMATION_STRATEGIES).map(strategy => strategy.key);
  // If the current strategy is not valid, default to SPIDER.
  if (!validStrategies.includes(currentStrategy?.automation_strategy)) {
    return AUTOMATION_STRATEGIES.SPIDER.key;
  }

  // If the current strategy is KOBE and not eligible, default to SPIDER.
  if (currentStrategy?.automation_strategy === AUTOMATION_STRATEGIES.KOBE.key && !currentStrategy?.is_kobe_eligible) {
    return AUTOMATION_STRATEGIES.SPIDER.key;
  }

  return currentStrategy?.automation_strategy;
};

function AutomationForm({ automationData, lobsangEventId, kobeZones }) {
  const classes = useStyles();
  const currentAutomation = automationData?.current_strategy;
  const initialStrategy = getInitialStrategy(automationData);

  const getSpiderFormData = () => {
    if (
      currentAutomation?.automation_strategy === AUTOMATION_STRATEGIES.SPIDER.key ||
      currentAutomation?.automation_strategy === LINE_SEGMENT_SPIDER
    ) {
      return automationData?.current_strategy;
    }
    if (currentAutomation?.spider_exclusion) {
      return { spider_exclusion: currentAutomation.spider_exclusion };
    }
    return null;
  };
  const [selectedStrategy, setSelectedStrategy] = useState(initialStrategy);
  // Mutation hook for updating automation.
  const {
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
    isLoading: isUpdateLoading,
    reset: resetUpdateState,
    mutate: updateAutomation,
  } = useUpdateAutomation();

  const {
    isSuccess: isUpdateExpectationsSuccess,
    isError: isUpdateExpectationsError,
    isLoading: isUpdateExpectationsLoading,
    reset: resetUpdateExpectationsState,
    mutate: updateExpectations,
  } = useUpdateExpectations();

  // Handle submission.
  const handleSubmit = formValues => {
    const newAutomation = {
      automation_strategy: selectedStrategy,
      ...formValues,
    };

    updateAutomation({
      lobsangEventId,
      newAutomation,
    });
  };

  // Handle Expectations submission.
  const handleSubmitExpectations = formValues => {
    const newExpectations = {
      ...formValues,
    };

    updateExpectations({
      lobsangEventId,
      newExpectations,
    });
  };

  // Clear the success message if the user interacts with the expectations form.
  const handleResetExpectations = () => {
    resetUpdateExpectationsState();
  };

  // Clear the success message if the user interacts with the form.
  const handleReset = () => {
    resetUpdateState();
  };

  // Clear the success message after 5 seconds
  useEffect(() => {
    let timeoutId;
    if (isUpdateSuccess) {
      timeoutId = setTimeout(() => {
        resetUpdateState();
      }, 5000);
    }
    return () => clearTimeout(timeoutId);
  }, [isUpdateSuccess]);

  // Clear the success expectations message after 5 seconds
  useEffect(() => {
    let timeoutId;
    if (isUpdateExpectationsSuccess) {
      timeoutId = setTimeout(() => {
        resetUpdateExpectationsState();
      }, 5000);
    }
    return () => clearTimeout(timeoutId);
  }, [isUpdateExpectationsSuccess]);

  return (
    <Box className={classes.container}>
      <Box className={classes.strategySelectorContainer}>
        <RadioGroup
          label={
            <>
              Please select the type of request.
              <span className={classes.required}>* </span>
              (Required)
            </>
          }
          row
          value={selectedStrategy}
          onChange={event => setSelectedStrategy(event.target.value)}
        >
          {Object.values(AUTOMATION_STRATEGIES).map(strategy => (
            <Radio
              key={strategy.key}
              label={
                <>
                  {strategy.label}
                  {strategy.key === AUTOMATION_STRATEGIES.KOBE.key && !currentAutomation?.is_kobe_eligible ? (
                    <span> (NOT ELIGIBLE)</span>
                  ) : null}
                  {strategy.key === AUTOMATION_STRATEGIES.KOBE_ZONE.key && !currentAutomation?.zone_kobe_eligible && (
                    <span> (NOT ELIGIBLE)</span>
                  )}
                  {strategy.key === currentAutomation?.automation_strategy ? (
                    <span className={classes.required}> (PRICED)</span>
                  ) : null}
                </>
              }
              value={strategy.key}
              disabled={
                (strategy.key === AUTOMATION_STRATEGIES.KOBE.key && !currentAutomation?.is_kobe_eligible) ||
                (strategy.key === AUTOMATION_STRATEGIES.KOBE_ZONE.key && !currentAutomation?.zone_kobe_eligible)
              }
            />
          ))}
        </RadioGroup>

        <Divider />
      </Box>

      {selectedStrategy === AUTOMATION_STRATEGIES.SPIDER.key ? (
        <>
          <ExpectationsForm
            automationData={currentAutomation}
            isError={isUpdateExpectationsError}
            isLoading={isUpdateExpectationsLoading}
            isSuccess={isUpdateExpectationsSuccess}
            onSubmit={handleSubmitExpectations}
            onReset={handleResetExpectations}
          />
          <hr style={{ border: '.5px solid #D9DDDF', width: '100%' }} />
          <SpiderForm
            automationData={getSpiderFormData()}
            isError={isUpdateError}
            isLoading={isUpdateLoading}
            isSuccess={isUpdateSuccess}
            onSubmit={handleSubmit}
            onReset={handleReset}
          />
        </>
      ) : null}

      {selectedStrategy === AUTOMATION_STRATEGIES.KOBE.key ? (
        <KobeForm
          automationData={
            currentAutomation?.automation_strategy === AUTOMATION_STRATEGIES.KOBE.key
              ? automationData?.current_strategy
              : null
          }
          isError={isUpdateError}
          isLoading={isUpdateLoading}
          isSuccess={isUpdateSuccess}
          kobeZones={kobeZones}
          proxyEvents={automationData?.proxy_events?.[AUTOMATION_STRATEGIES.KOBE.key] ?? []}
          onSubmit={handleSubmit}
          onReset={handleReset}
        />
      ) : null}

      {selectedStrategy === AUTOMATION_STRATEGIES.KOBE_ZONE.key ? (
        <ZoneKobeForm
          automationData={
            currentAutomation?.automation_strategy === AUTOMATION_STRATEGIES.KOBE_ZONE.key
              ? automationData?.current_strategy
              : null
          }
          isError={isUpdateError}
          isLoading={isUpdateLoading}
          isSuccess={isUpdateSuccess}
          kobeZones={Object.keys(automationData?.proxy_events?.[AUTOMATION_STRATEGIES.KOBE_ZONE.key])}
          proxyEvents={automationData?.proxy_events?.[AUTOMATION_STRATEGIES.KOBE_ZONE.key] ?? {}}
          onSubmit={handleSubmit}
          onReset={handleReset}
        />
      ) : null}
    </Box>
  );
}

AutomationForm.propTypes = propTypes;
AutomationForm.defaultProps = defaultProps;

export default AutomationForm;
